import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, MapPin, Maximize, BedDouble, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Kicker, inputCls } from "@/components/form";
import { api } from "@/lib/api";

const DEALS = [["", "Alle"], ["kauf", "Kaufen"], ["miete", "Mieten"], ["gewerbe", "Gewerbe"], ["grundstueck", "Grundstück"]];
export const fmtPrice = (p, deal) => p ? `${new Intl.NumberFormat("de-DE").format(p)} €${deal === "miete" || deal === "gewerbe" ? " /Monat" : ""}` : "auf Anfrage";

export default function Immobilien() {
  const [items, setItems] = useState([]);
  const [deal, setDeal] = useState("");
  const [city, setCity] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = {};
    if (deal) params.deal_type = deal;
    if (city) params.city = city;
    if (maxPrice) params.max_price = maxPrice;
    api.get("/properties", { params }).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [deal]);

  return (
    <div data-testid="immobilien-page" className="bg-navy-900">
      <Seo title="Immobilien" description="Aktuelle Immobilienangebote von VM-FFM.DE – Häuser, Wohnungen, Gewerbe und Grundstücke im Rhein-Main-Gebiet kaufen oder mieten." path="/immobilien" keywords="Immobilien Frankfurt, Haus kaufen, Wohnung mieten, Gewerbeimmobilie, Grundstück, Immobilienmakler Rhein-Main" ogImage="https://static.prod-images.emergentagent.com/jobs/9cdaf805-ebb2-4698-b869-6dae56934adb/images/3e9ea73796e193b55ddcf3fdeee745a56cc5bd7711ae40226430f69bb8bace8e.jpeg" />
      <section className="pt-36 pb-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Kicker>Immobilienangebote</Kicker>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-white leading-none">Ihre neue Immobilie.</h1>
          <p className="mt-5 text-slate-300 max-w-2xl leading-relaxed">Entdecken Sie ausgewählte Objekte im Rhein-Main-Gebiet – kuratiert und persönlich betreut.</p>

          <div className="mt-8 bg-navy-800 border border-white/10 rounded-md p-5" data-testid="property-search">
            <div className="flex flex-wrap gap-2 mb-4">
              {DEALS.map(([v, l]) => (
                <button key={v} data-testid={`filter-${v || "all"}`} onClick={() => setDeal(v)}
                  className={`px-4 py-2 rounded-sm text-sm font-medium transition-colors ${deal === v ? "bg-gold-500 text-navy-900" : "bg-navy-900 text-slate-300 hover:text-white"}`}>{l}</button>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input data-testid="search-city" className={inputCls} placeholder="Ort (z. B. Frankfurt)" value={city} onChange={(e) => setCity(e.target.value)} />
              <input data-testid="search-price" type="number" className={inputCls} placeholder="Max. Preis (€)" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} />
              <button data-testid="search-btn" onClick={load} className="bg-gold-500 text-navy-900 font-bold rounded-sm px-6 py-3 flex items-center justify-center gap-2 hover:bg-gold-300 transition-colors"><Search size={16} /> Suchen</button>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {loading ? <p className="text-slate-400">Lädt…</p> : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {items.map((p, i) => (
                <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}>
                  <Link to={`/immobilien/${p.id}`} data-testid={`property-card-${p.id}`} className="group block bg-navy-800 border border-white/5 rounded-md overflow-hidden hover:-translate-y-1 hover:border-gold-500/40 transition-[transform,border-color] duration-300">
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img src={p.image_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <span className="absolute top-3 left-3 bg-gold-500 text-navy-900 text-xs font-bold px-3 py-1 rounded-sm uppercase tracking-wide">{DEALS.find((d) => d[0] === p.deal_type)?.[1]}</span>
                      {p.status !== "verfuegbar" && <span className="absolute top-3 right-3 bg-navy-900/90 text-gold-300 text-xs px-3 py-1 rounded-sm">{p.status}</span>}
                    </div>
                    <div className="p-6">
                      <p className="text-gold-500 font-serif text-2xl">{fmtPrice(p.price, p.deal_type)}</p>
                      <h3 className="mt-1 text-lg font-bold text-white">{p.title}</h3>
                      <p className="text-sm text-slate-400 flex items-center gap-1.5 mt-2"><MapPin size={14} /> {p.location}, {p.city}</p>
                      <div className="mt-4 flex items-center gap-4 text-xs text-slate-400 border-t border-white/5 pt-4">
                        {p.size_sqm > 0 && <span className="flex items-center gap-1.5"><Maximize size={13} /> {p.size_sqm} m²</span>}
                        {p.rooms > 0 && <span className="flex items-center gap-1.5"><BedDouble size={13} /> {p.rooms} Zimmer</span>}
                        {p.energy_class && <span className="ml-auto text-gold-500">Energie {p.energy_class}</span>}
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
              {items.length === 0 && <p className="text-slate-500 col-span-full py-10 text-center">Keine Objekte gefunden. Bitte Filter anpassen.</p>}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
